import React, { useState, useMemo } from "react";
import './CustomerDueDiligence.css';

const data = [
    // ================= AI GOVERNANCE =================
    {
        id: "ai-overview",
        title: "AI System Operation & Architecture",
        subsections: [
            {
                title: "Explanation of How the AI System Works",
                controls: [
                    "Analyzes UI/UX design elements to detect potential dark patterns using multimodal agents",
                    "Uses dynamic context-based considerations to examine key dark patterns",
                    "Figma plugin extracts Frame as image",
                    "Metadata passed to detection engine for evaluation",
                    "Outputs generated based on harm assessment",
                    "Explanations reference specific design features triggering detection",
                    "Third-party LLMs (Gemini, GPT) leveraged as tools within detection agents",
                ],
                automation: [
                    { type: "Automated", item: "Multimodal detection engine processing" },
                    { type: "Automated", item: "Metadata extraction from Figma environment" },
                    { type: "Manual", item: "Model validation and governance oversight" },
                ],
            },
        ],
    },
    {
        id: "ai-security",
        title: "Preventing Malicious Access or Data Compromise",
        subsections: [
            {
                title: "Security Safeguards",
                controls: [
                    "Plugin operates strictly within Figma environment",
                    "Processes only metadata relevant to dark pattern detection",
                    "No raw customer data or unrelated assets accessed",
                    "TLS 1.2/1.3 encryption in transit",
                    "AES-256 encryption at rest",
                    "Strict IAM policies restrict scope-bound access",
                ],
                automation: [
                    { type: "Automated", item: "TLS encryption enforcement" },
                    { type: "Automated", item: "AES-256 storage encryption" },
                    { type: "Automated", item: "IAM scope-based access restriction" },
                ],
            },
        ],
    },
    {
        id: "ai-objectives",
        title: "Objectives of AI Implementation",
        subsections: [
            {
                title: "Strategic Objectives",
                controls: [
                    "Detect and flag dark patterns in digital product designs",
                    "Provide explainable feedback to designers",
                    "Support regulatory compliance documentation",
                    "Enhance trust and transparency in workflows",
                ],
                automation: [],
            },
        ],
    },
    {
        id: "ai-explainability",
        title: "Explainability & Interpretability Controls",
        subsections: [
            {
                title: "Explainability Controls",
                controls: [
                    "Feature attribution identifies triggering design elements",
                    "Rule-based justification accompanies ML outputs",
                    "Audit logs maintain traceability of detections",
                    "Explanations visible directly to customers",
                ],
                automation: [
                    { type: "Automated", item: "Feature attribution generation" },
                    { type: "Automated", item: "Audit log recording" },
                ],
            },
        ],
    },
    {
        id: "risk-management",
        title: "Risk Management & Issue Remediation",
        subsections: [
            {
                title: "Risk Lifecycle Controls",
                controls: [
                    "Pre-deployment risk assessment (performance, functional, failure risks)",
                    "Cybersecurity and hallucination risk monitoring",
                    "Centralized ticketing for issue tracking",
                    "Critical vulnerability SLA ≤ 48 hours",
                    "Regular vulnerability scans on GCP",
                ],
                automation: [
                    { type: "Automated", item: "Cloud vulnerability scanning" },
                    { type: "Manual", item: "Risk review & remediation oversight" },
                ],
            },
        ],
    },
    {
        id: "third-party-reporting",
        title: "Third-Party Vulnerability & Bias Reporting",
        subsections: [
            {
                title: "Responsible Disclosure Process",
                controls: [
                    "Dedicated feedback channel for vulnerability/bias reporting",
                    "All submissions logged and triaged",
                    "Prompt acknowledgment and investigation",
                ],
                automation: [
                    { type: "Manual", item: "Submission triage & review" },
                ],
            },
        ],
    },
    {
        id: "notification",
        title: "Post-Implementation Issue Notification",
        subsections: [
            {
                title: "Customer Notification Protocol",
                controls: [
                    "Immediate notification upon identified vulnerabilities or risks",
                    "Severity classification provided",
                    "Impact assessment documented",
                    "Defined remediation timeline communicated",
                ],
                automation: [],
            },
        ],
    },
    {
        id: "developer-fairness",
        title: "Developer Fairness Assurance Measures",
        subsections: [
            {
                title: "Design-Time Fairness Controls",
                controls: [
                    "Curated training datasets with diverse UI examples",
                    "Consumer-focused criteria embedded into detection rules",
                    "Measures to prevent hallucinations",
                    "Independent quality validation prior to release",
                ],
                automation: [
                    { type: "Manual", item: "Independent output validation review" },
                ],
            },
        ],
    },
    {
        id: "vendor-fairness",
        title: "Vendor-Level Fairness Governance",
        subsections: [
            {
                title: "Ongoing Fairness Oversight",
                controls: [
                    "Continuous monitoring of detection outputs",
                    "Separate feedback channel for drift/performance issues",
                    "Periodic updates to detection agents",
                    "Transparent documentation of decision logic",
                ],
                automation: [
                    { type: "Automated", item: "Monitoring for performance drift" },
                    { type: "Manual", item: "Model update governance review" },
                ],
            },
        ],
    },
    {
        id: "customer-fairness",
        title: "Customer Controls for Fairness",
        subsections: [
            {
                title: "Customer Review & Oversight",
                controls: [
                    "Customers review flagged elements with explanations",
                    "Ability to override false positives",
                    "Audit logs ensure traceability of detections",
                ],
                automation: [
                    { type: "Automated", item: "Audit log generation" },
                ],
            },
        ],
    },

    // ================= ENTERPRISE SECURITY =================
    {
        id: "logging",
        title: "Logging & Monitoring",
        subsections: [
            {
                title: "Overview",
                controls: [
                    "Remote-first model with centralized logging in GCP and Google Workspace",
                    "No sensitive data stored locally on BYOD devices",
                    "Centralized logging across IAM, APIs, workloads, and Workspace",
                    "NTP-based time synchronization for forensic integrity",
                    "Defined incident response workflow triggered by alerts",
                ],
                automation: [
                    { type: "Automated", item: "Centralized GCP logging & SIEM" },
                    { type: "Automated", item: "Real-time anomaly detection" },
                    { type: "Automated", item: "Immutable log storage" },
                    { type: "Manual", item: "Alert triage and root cause analysis" },
                    { type: "Guidance-Based", item: "BYOD local logging compliance" },
                ],
            },
        ],
    },
    {
        id: "vulnerability",
        title: "Vulnerability & Patch Management",
        subsections: [
            {
                title: "Controls",
                controls: [
                    "Documented vulnerability management program",
                    "Monthly vulnerability scans",
                    "Annual external penetration testing",
                    "Critical remediation SLA ≤ 48 hours",
                    "Weekly vulnerability tracking review",
                    "Automated dependency monitoring (CVE)",
                    "Staging validation prior to production deployment",
                ],
                automation: [
                    { type: "Automated", item: "GCP vulnerability scanning" },
                    { type: "Automated", item: "Automated patch detection" },
                    { type: "Automated", item: "Cloud auto-patching" },
                    { type: "Manual", item: "External penetration testing" },
                    { type: "Manual", item: "Remediation oversight workflow" },
                ],
            },
        ],
    },
    {
        id: "access-control",
        title: "Access Control",
        subsections: [
            {
                title: "Governance & Enforcement",
                controls: [
                    "RBAC enforced via Google Cloud IAM",
                    "Least privilege enforcement",
                    "Mandatory MFA for all users",
                    "Quarterly user access review",
                    "Monthly privileged access review",
                    "Inactive account disablement within 90 days",
                    "15-minute session timeout enforcement",
                    "Time-bound emergency access process",
                ],
                automation: [
                    { type: "Automated", item: "IAM RBAC enforcement" },
                    { type: "Automated", item: "MFA enforcement" },
                    { type: "Automated", item: "Access change logging" },
                    { type: "Manual", item: "Approval workflows & review validation" },
                ],
            },
        ],
    },
    {
        id: "cloud-governance",
        title: "Cloud Governance",
        subsections: [
            {
                title: "Architecture & Segregation",
                controls: [
                    "Public cloud SaaS delivery model (GCP)",
                    "Project and VPC-level segregation",
                    "Encryption via GCP KMS (AES-256)",
                    "TLS 1.2/1.3 in transit",
                    "SSO and federated identity enforcement",
                    "Access Transparency logging",
                    "Certified data removal upon termination",
                ],
                automation: [
                    { type: "Automated", item: "IAM segregation enforcement" },
                    { type: "Automated", item: "Encryption enforcement via KMS" },
                    { type: "Automated", item: "Cloud-native monitoring" },
                    { type: "Manual", item: "Periodic CSP access reviews" },
                ],
            },
        ],
    },
    {
        id: "incident-management",
        title: "Incident Management",
        subsections: [
            {
                title: "Incident Response Framework",
                controls: [
                    "Documented incident response plan",
                    "Defined triage, containment, segregation procedures",
                    "Root cause analysis documentation",
                    "Breach notification workflow aligned to GDPR/DPDP/HIPAA/PCI",
                    "Threat intelligence monitoring (CVE feeds)",
                    "No reportable breaches in last 3 years",
                ],
                automation: [
                    { type: "Automated", item: "GCP alert integration" },
                    { type: "Automated", item: "Log-based anomaly detection" },
                    { type: "Manual", item: "Incident triage & RCA" },
                    { type: "Manual", item: "Breach communication workflow" },
                ],
            },
        ],
    },
    {
        id: "bcdr",
        title: "Business Continuity & Disaster Recovery",
        subsections: [
            {
                title: "Resilience & Testing",
                controls: [
                    "Documented BC/DR policy",
                    "Annual Business Impact Analysis",
                    "Annual BC/DR tabletop & failover testing",
                    "Cloud regional redundancy",
                    "Cyber insurance coverage",
                ],
                automation: [
                    { type: "Automated", item: "Cloud failover & redundancy" },
                    { type: "Automated", item: "Snapshot restoration" },
                    { type: "Manual", item: "BIA documentation" },
                ],
            },
        ],
    },
    {
        id: "sdlc",
        title: "Secure SDLC",
        subsections: [
            {
                title: "Development & Maintenance",
                controls: [
                    "Secure SDLC aligned with OWASP & NIST",
                    "Threat modeling & secure design review",
                    "Dev/Test/Prod separation",
                    "No production data in non-production",
                    "Static & dynamic analysis",
                    "Open-source dependency review",
                    "Formal change management & rollback",
                ],
                automation: [
                    { type: "Automated", item: "CI/CD pipeline enforcement" },
                    { type: "Automated", item: "Dependency scanning" },
                    { type: "Automated", item: "Cloud rollback snapshots" },
                    { type: "Manual", item: "Secure code review oversight" },
                ],
            },
        ],
    },
];

const badgeClassMap = {
    "Automated": "cdd-badge-automated",
    "Manual": "cdd-badge-manual",
    "Inherited": "cdd-badge-inherited",
    "Guidance-Based": "cdd-badge-guidance",
};

function Badge({ type }) {
    return (
        <span className={`cdd-badge ${badgeClassMap[type] || ''}`}>
            {type}
        </span>
    );
}

export default function CustomerDueDiligence() {
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search) return data;
        const q = search.toLowerCase();

        return data
            .map((section) => {
                const sectionTitleMatch = section.title.toLowerCase().includes(q);

                const filteredSubs = section.subsections
                    .map((sub) => {
                        const subTitleMatch = sub.title.toLowerCase().includes(q);

                        // If section or subsection title matches, keep all content inside
                        if (sectionTitleMatch || subTitleMatch) {
                            return sub;
                        }

                        // Otherwise filter individual controls & automation items
                        const matchedControls = sub.controls.filter((c) =>
                            c.toLowerCase().includes(q)
                        );
                        const matchedAutomation = sub.automation.filter((a) =>
                            a.item.toLowerCase().includes(q)
                        );

                        if (matchedControls.length === 0 && matchedAutomation.length === 0) {
                            return null; // no match at all in this subsection
                        }

                        return {
                            ...sub,
                            controls: matchedControls,
                            automation: matchedAutomation,
                        };
                    })
                    .filter(Boolean);

                if (filteredSubs.length === 0) return null;

                return { ...section, subsections: filteredSubs };
            })
            .filter(Boolean);
    }, [search]);

    const handleNavClick = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="cdd-container">
            <aside className="cdd-sidebar">
                <h2>Navigation</h2>
                <ul>
                    {data.map((s) => (
                        <li key={s.id}>
                            <a href={`#${s.id}`} onClick={(e) => handleNavClick(e, s.id)}>{s.title}</a>
                        </li>
                    ))}
                </ul>
            </aside>

            <main className="cdd-main">
                <h1>Enterprise AI Governance &amp; Security Framework</h1>

                <input
                    type="text"
                    placeholder="Search controls..."
                    className="cdd-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {filtered.map((section) => (
                    <div key={section.id} id={section.id} className="cdd-section">
                        <div className="cdd-section-heading">
                            {section.title}
                        </div>

                        <div className="cdd-subsections">
                            {section.subsections.map((sub, idx) => (
                                <div key={idx} className="cdd-card">
                                    <h3>{sub.title}</h3>

                                    <div>
                                        <h4 className="cdd-controls-heading">Controls</h4>
                                        <ul className="cdd-controls-list">
                                            {sub.controls.map((control, i) => (
                                                <li key={i}>{control}</li>
                                            ))}
                                        </ul>
                                    </div>

                                    {sub.automation.length > 0 && (
                                        <div>
                                            <h4 className="cdd-automation-heading">
                                                Automation Mapping
                                            </h4>
                                            <ul className="cdd-automation-list">
                                                {sub.automation.map((a, i) => (
                                                    <li key={i} className="cdd-automation-item">
                                                        <Badge type={a.type} />
                                                        <span>{a.item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </main>
        </div>
    );
}
